import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Modal,
  IconButton,
  Chip,
} from "@mui/material";
import {
  AccountBalanceWallet as SafeIcon,
  Payments as CashIcon,
  HowToReg as AdminIcon,
  CardMembership as PassIcon,
  Close as CloseIcon,
  OpenInNew as DetailsIcon,
} from "@mui/icons-material";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95vw", sm: "80vw", md: "70vw" },
  maxHeight: "85vh",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pl-PL", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const DetailModal = ({ open, onClose, title, rows, type, formatCurrency }) => (
  <Modal open={open} onClose={onClose}>
    <Box sx={modalStyle}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="h6">{title}</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>
      <Box sx={{ overflow: "auto", flex: 1 }}>
        {rows.length === 0 ? (
          <Typography sx={{ p: 3 }} color="text.secondary">Brak rekordów.</Typography>
        ) : type === "passes" ? (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Klient</TableCell>
                  <TableCell>Typ karnetu</TableCell>
                  <TableCell>Data zakupu</TableCell>
                  <TableCell>Lokalizacja</TableCell>
                  <TableCell align="right">Kwota</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.pass_id} hover>
                    <TableCell>{r.user_name}</TableCell>
                    <TableCell>{r.pass_type_name}</TableCell>
                    <TableCell>{fmtDate(r.created_at)}</TableCell>
                    <TableCell>{r.location_name || <Chip label="brak" size="small" />}</TableCell>
                    <TableCell align="right">{formatCurrency(r.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Klient</TableCell>
                  <TableCell>Zajęcia</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Lokalizacja</TableCell>
                  {type === "admin" && <TableCell>Zatwierdził</TableCell>}
                  <TableCell align="right">Kwota</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={`${r.user_id}-${r.lesson_id}`} hover>
                    <TableCell>{r.user_name}</TableCell>
                    <TableCell>{r.lesson_name}</TableCell>
                    <TableCell>{fmtDate(r.lesson_date)}</TableCell>
                    <TableCell>{r.location_name || <Chip label="brak" size="small" />}</TableCell>
                    {type === "admin" && <TableCell>{r.cleared_by_name || "—"}</TableCell>}
                    <TableCell align="right">{formatCurrency(r.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  </Modal>
);

// "Sejf" view: how much cash should physically be in the safe for the selected
// date range. Cash = on-site single entries + admin-cleared entries + cash-paid
// passes. PayU and online-paid passes are excluded (they go to the bank).
const SafeReconciliation = ({ safeData, formatCurrency }) => {
  const [detailModal, setDetailModal] = useState(null); // null | "cash" | "admin" | "passes"

  if (!safeData) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
        Brak danych do wyświetlenia
      </Typography>
    );
  }

  const {
    locations = [],
    total_cash_entries = 0,
    total_admin_cleared = 0,
    total_cash_passes = 0,
    unattributed_cash_passes = 0,
    cash_entries_detail = [],
    admin_cleared_detail = [],
    cash_passes_detail = [],
  } = safeData;

  const safe_total = total_cash_entries + total_admin_cleared;

  const breakdown = [
    {
      key: "cash",
      label: "Wejścia gotówka",
      value: total_cash_entries,
      icon: <CashIcon />,
      detail: cash_entries_detail,
      type: "cash",
    },
    {
      key: "admin",
      label: "Oznaczone przez admina",
      value: total_admin_cleared,
      icon: <AdminIcon />,
      detail: admin_cleared_detail,
      type: "admin",
    },
    {
      key: "passes",
      label: "Karnety gotówką",
      value: total_cash_passes,
      icon: <PassIcon />,
      detail: cash_passes_detail,
      type: "passes",
    },
  ];

  const activeModal = breakdown.find((b) => b.key === detailModal);

  return (
    <Box>
      {/* Headline: expected cash in the safe */}
      <Card
        sx={{
          mb: 3,
          color: "#fff",
          background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
        }}
      >
        <CardContent sx={{ textAlign: "center", py: 4 }}>
          <SafeIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            Powinno być w sejfie
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800 }}>
            {formatCurrency(safe_total)}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            Gotówka + wejścia oznaczone przez admina w wybranym okresie
          </Typography>
        </CardContent>
      </Card>

      {/* Breakdown by payment source — clickable */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {breakdown.map((b) => (
          <Grid item xs={12} sm={4} key={b.label}>
            <Paper
              sx={{
                p: 2,
                height: "100%",
                cursor: b.detail.length > 0 ? "pointer" : "default",
                transition: "box-shadow 0.15s",
                "&:hover": b.detail.length > 0 ? { boxShadow: 4 } : {},
              }}
              onClick={() => b.detail.length > 0 && setDetailModal(b.key)}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", color: "text.secondary" }}>
                  {b.icon}
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {b.label}
                  </Typography>
                </Box>
                {b.detail.length > 0 && (
                  <DetailsIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                )}
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {formatCurrency(b.value)}
              </Typography>
              {b.detail.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {b.detail.length} {b.type === "passes" ? "karnetów" : "wejść"}
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {unattributed_cash_passes > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Karnety gotówką bez przypisania do lokalizacji (obejmują wiele lokalizacji):{" "}
          <strong>{formatCurrency(unattributed_cash_passes)}</strong>. Wliczone do sumy powyżej.
        </Alert>
      )}

      {/* Per-location table */}
      <Paper sx={{ p: { xs: 1, sm: 2 } }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Gotówka wg lokalizacji
        </Typography>
        {locations.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Brak gotówkowych płatności w tym okresie.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Lokalizacja</TableCell>
                  <TableCell align="right">Wejścia gotówka</TableCell>
                  <TableCell align="right">Admin</TableCell>
                  <TableCell align="right">Karnety</TableCell>
                  <TableCell align="right">Razem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.map((loc) => (
                  <TableRow key={loc.location_id}>
                    <TableCell>{loc.location_name}</TableCell>
                    <TableCell align="right">{formatCurrency(loc.cash_entries)}</TableCell>
                    <TableCell align="right">{formatCurrency(loc.admin_cleared_amount)}</TableCell>
                    <TableCell align="right">{formatCurrency(loc.cash_passes)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatCurrency(loc.location_total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {activeModal && (
        <DetailModal
          open={true}
          onClose={() => setDetailModal(null)}
          title={activeModal.label}
          rows={activeModal.detail}
          type={activeModal.type}
          formatCurrency={formatCurrency}
        />
      )}
    </Box>
  );
};

export default SafeReconciliation;
